import type { ICommandBarItemProps } from '@fluentui/react';
import { IAppContext } from 'App/AppContext';
import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import { getPeriodFilter } from 'components/Filter/PeriodFilterHelper';
import { TFunction } from 'i18next';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { processStartTemplate } from 'models/copilot/actions/aiActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContentBase } from 'models/copilot/copilotBridge';
import {
  ContentBlockKind,
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import {
  CopilotChatInputUsageProps,
  CopilotPanelContextDefinition,
  CopilotPanelEntityRef,
  CopilotPanelProcessCompletionRef,
  CopilotPanelProcessState,
  CopilotPanelProgressRef,
  CopilotPanelRuntime,
  CopilotPanelRuntimeView,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { CopilotControlAuditContent } from 'models/copilot/Processes/ControlEvidenceAudit/copilotControlEvidenceAudit';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import {
  apiCopilotExecuteProcessControlDocumentationAudit,
  apiCopilotExecuteProcessControlEvidenceAudit,
} from 'services/Api/copilotService';
import { ProgressHubProcess } from 'services/SignalR/ProgressHub';
import { newGuid } from 'utils/guid';
import { getLocalStorageData, LocalStorageKeys } from 'utils/localstorage';
import { AutomationDraftingProcess } from '../AutomationDrafting/automationDraftingProcess';
import { getEntityDetailBindings, withSceneHelpFirst } from '../Generic/genericSceneBindings';

const standardsSelectionBlockTitle = 'controlAuditStandardsSelection';
const startActionName = 'controlAuditStart';

// The control audits (evidence, documentation) run under 1+ of the control's linked standards. The
// standards are settled BEFORE the first execute: none refuses, one is taken with a message naming
// it, several are asked with a multi-select card. The server freezes the choice at preparation, so
// later executes of the same run only carry it along.
export class CopilotControlAuditPanelContextDefinition extends CopilotPanelContextDefinition {
  constructor() {
    super();
    this.supportsChat = true;
    this.requiresActiveAction = true;
  }

  getBindings(content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    const bindings: AIActionBinding[] = [
      createAIActionBinding(processStartTemplate, { processId: TenantProcess.ControlDocumentationAudit }),
      createAIActionBinding(processStartTemplate, { processId: TenantProcess.ControlEvidenceAudit }),
    ];
    // Draft automation for the control the scene shows (the drafting process, keyed on the control)
    if (content instanceof CopilotControlAuditContent && content.canDraftAutomation) {
      bindings.push(createAIActionBinding(processStartTemplate, { processId: TenantProcess.AutomationDrafting }));
    }
    // the same questions as the control in plain chat; Draft automation is listed above
    if (content instanceof CopilotControlAuditContent) {
      bindings.push(...getEntityDetailBindings(EntityTypes.Control, content.controlId));
    }

    return withSceneHelpFirst(bindings);
  }

  getProgressRef(runtime: CopilotPanelRuntimeView): CopilotPanelProgressRef | undefined {
    if (AutomationDraftingProcess.isRunning(runtime)) {
      return AutomationDraftingProcess.getProgressRef(runtime, this.getEntityRef(runtime.content));
    }

    const action = this.getSelectedRunningControlAuditAction(runtime);
    if (!action?.processId) return undefined;

    const entityRef = this.getEntityRef(runtime.content);
    if (!entityRef) return undefined;

    return {
      ...entityRef,
      tenantProcess: action.processId,
      hubProcess:
        action.processId === TenantProcess.ControlDocumentationAudit
          ? ProgressHubProcess.ControlDocumentationAudit
          : ProgressHubProcess.ControlEvidenceAudit,
    };
  }

  getProcessCompletionRefAfterEntitySelection(
    runtime: Pick<CopilotPanelRuntime, 'action' | 'actions' | 'content'>,
  ): CopilotPanelProcessCompletionRef | undefined {
    const action = this.getSelectedRunningControlAuditAction(runtime);
    if (!action?.processId) return undefined;

    const entityRef = this.getEntityRef(runtime.content);
    if (!entityRef) return undefined;

    return { ...entityRef, processId: action.processId };
  }

  private getSelectedRunningControlAuditAction(
    runtime: Pick<CopilotPanelRuntime, 'action'>,
  ): CopilotAction | undefined {
    if (runtime.action?.status === TenantProcessStatus.InProgress && this.isControlAuditAction(runtime.action)) {
      return runtime.action;
    }

    return undefined;
  }

  async getAnswer(runtime: CopilotPanelRuntime, t: TFunction<string[]>): Promise<void> {
    const { action } = runtime;
    if (AutomationDraftingProcess.isRunning(runtime)) {
      await AutomationDraftingProcess.execute(runtime, this.getEntityRef(runtime.content));

      return;
    }

    if (!action || !this.isControlAuditAction(action)) {
      return;
    }

    const content = runtime.content as CopilotControlAuditContent | undefined;
    if (!content?.controlId) return;

    const normIds = this.resolveStandards(runtime, content, t);
    // asked in the chat, or refused: the card submit re-enters here
    if (normIds === undefined) return;
    content.normIds = normIds;

    runtime.setIsLoading(true);
    const { periodStart, periodEnd } = this.resolveAuditPeriod(runtime.appContext);
    const executeProcess =
      action.processId === TenantProcess.ControlDocumentationAudit
        ? apiCopilotExecuteProcessControlDocumentationAudit
        : apiCopilotExecuteProcessControlEvidenceAudit;
    const answer = await executeProcess(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      content.controlId,
      [],
      runtime.connectionId,
      runtime.accessToken,
      periodStart,
      periodEnd,
      normIds,
    );
    if (!answer) return;

    // the server settles the standards at preparation (a panel that sent none gets the control's
    // linked standards back): carry its answer on the next execute of this run
    if (answer.normIds.length > 0) content.normIds = answer.normIds;

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
  }

  // Starting the audit is this usage's own card action: the standards chosen on the card go onto this
  // conversation's block first, then the panel runs getAnswer, which reads them back. Any other card
  // returns false so the shared panel behaviour is untouched.
  async onAdaptiveCardSubmit(
    runtime: CopilotPanelRuntime,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
    _t: TFunction<string[]>,
  ): Promise<boolean> {
    if (AutomationDraftingProcess.isRunning(runtime)) {
      return AutomationDraftingProcess.onAdaptiveCardSubmit(runtime, block, payload, _t);
    }

    if (payload.actionData.action !== startActionName) return false;

    this.applySubmittedInputs(runtime, block.id, payload.inputs);
    runtime.setConversation(runtime.conversation.clone());

    return true;
  }

  // The standards this run goes under, or undefined when the chat has to settle them first. In order:
  // the answered card in the thread (it survives panel reloads, the conversation is part of the
  // persisted state); for a run under way what this content carries (the server froze the choice at
  // preparation, so an empty list is fine and nothing is asked again); otherwise the candidates of a
  // fresh run: none refuses, one is taken and announced, several are asked. A finished run's choice
  // is never reused silently: clicking the action again asks again.
  private resolveStandards(
    runtime: CopilotPanelRuntime,
    content: CopilotControlAuditContent,
    t: TFunction<string[]>,
  ): number[] | undefined {
    const fromCard = this.getStandardsSelection(runtime);
    if (fromCard) return fromCard;
    if (runtime.action?.status === TenantProcessStatus.InProgress) return content.normIds;

    const candidates = content.candidateNormIds;
    if (candidates.length === 0) {
      this.addMessage(runtime, t('copilot:ControlAudit.NoStandards'));

      return undefined;
    }

    if (candidates.length === 1) {
      this.addMessage(runtime, t('copilot:ControlAudit.SingleStandard', { name: this.getStandardName(runtime, candidates[0]) }));

      return [...candidates];
    }

    this.addStandardsSelectionQuestion(runtime, candidates, t);

    return undefined;
  }

  private getStandardsSelection(runtime: CopilotPanelRuntime): number[] | undefined {
    for (let index = runtime.conversation.thread.length - 1; index >= 0; index--) {
      const item = runtime.conversation.thread[index];
      const block = item.contentBlocks?.find(
        (candidate) =>
          candidate.kind === ContentBlockKind.AdaptiveCard && candidate.title === standardsSelectionBlockTitle,
      );
      if (!block?.adaptiveInputs) continue;

      // a multi-select choice set submits its values comma-joined; an empty submit is not a choice
      const normIds = (block.adaptiveInputs['selectedStandards'] ?? '')
        .split(',')
        .map((value) => Number(value.trim()))
        .filter((value) => Number.isFinite(value) && value > 0);
      if (normIds.length === 0) continue;

      return normIds;
    }

    return undefined;
  }

  private getStandardName(runtime: CopilotPanelRuntime, normId: number): string {
    return runtime.appContext.globalDataCache.norms.items.find((norm) => norm.normId === normId)?.name ?? `#${normId}`;
  }

  // Pushed onto the conversation that the execute right after this reads, then published as a clone:
  // the object already rendered can be skipped as an identity no-op.
  private addMessage(runtime: CopilotPanelRuntime, text: string): void {
    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.Copilot;
    item.response = text;
    runtime.conversation.thread.push(item);
    runtime.setConversation(runtime.conversation.clone());
  }

  private addStandardsSelectionQuestion(runtime: CopilotPanelRuntime, candidates: number[], t: TFunction<string[]>): void {
    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.CopilotQuestion;

    const block = new CopilotConversationContentBlock();
    block.id = newGuid();
    block.kind = ContentBlockKind.AdaptiveCard;
    block.title = standardsSelectionBlockTitle;
    block.adaptiveCardJson = JSON.stringify({
      $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
      type: 'AdaptiveCard',
      version: '1.5',
      body: [
        { type: 'TextBlock', text: t('copilot:ControlAudit.ChooseStandards'), wrap: true },
        {
          type: 'Input.ChoiceSet',
          id: 'selectedStandards',
          style: 'expanded',
          isMultiSelect: true,
          // every linked standard preselected: the common case is the combined audit
          value: candidates.join(','),
          choices: candidates.map((normId) => ({ title: this.getStandardName(runtime, normId), value: `${normId}` })),
        },
      ],
      actions: [
        {
          type: 'Action.Submit',
          title: t('copilot:ControlAudit.Confirm'),
          data: { action: startActionName },
        },
      ],
    });
    item.contentBlocks = [block];

    runtime.conversation.thread.push(item);
    runtime.setConversation(runtime.conversation.clone());
  }

  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (!this.isAvailableForContent(content)) return undefined;
    const auditContent = content as CopilotControlAuditContent;

    return { entityType: EntityTypes.Control, entityId: auditContent.controlId };
  }

  // the control audits never take typed input; the drafting process does in its refinement stage
  canStartChat(_actions: CopilotAction[], runtime?: CopilotPanelRuntimeView): boolean {
    if (runtime && AutomationDraftingProcess.isRunning(runtime)) return AutomationDraftingProcess.canStartChat(runtime);

    return false;
  }

  getChatInputUsageProps(runtime: CopilotPanelRuntime): CopilotChatInputUsageProps {
    return { ...super.getChatInputUsageProps(runtime), placeholder: AutomationDraftingProcess.getChatInputPlaceholder(runtime) };
  }

  getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    return AutomationDraftingProcess.getWelcomeMessage(action, t) ?? super.getWelcomeMessage(action, t);
  }

  getWelcomeMessages(action: CopilotAction, t: TFunction<string[]>): string[] {
    if (!this.isControlAuditAction(action)) {
      return super.getWelcomeMessages(action, t);
    }

    return [];
  }

  getPreviewCommands(runtime: CopilotPanelRuntime, t: TFunction<string[]>): ICommandBarItemProps[] {
    return AutomationDraftingProcess.getPreviewCommands(runtime, t);
  }

  applyProcessState(runtime: CopilotPanelRuntime, state: CopilotPanelProcessState): CopilotPreviewContent | undefined {
    return AutomationDraftingProcess.applyProcessState(runtime, state) ?? super.applyProcessState(runtime, state);
  }

  clearPreview(runtime: CopilotPanelRuntime): void {
    AutomationDraftingProcess.clearPreview(runtime);
  }

  getEntitySelectionQuestion(selectedEntityIds: number[], t: TFunction<string[]>): string {
    if (selectedEntityIds.length === 0) {
      return t('copilot:Processes.ControlAudit.Selection_zero');
    }

    if (selectedEntityIds.length === 1) {
      return t('copilot:Processes.ControlAudit.Selection_one');
    }

    return t('copilot:Processes.ControlAudit.Selection_other', { count: selectedEntityIds.length });
  }

  isPersistedProcessStateReady(state: CopilotPanelProcessState): boolean {
    if (!this.isControlAuditProcess(state.processId)) {
      return super.isPersistedProcessStateReady(state);
    }

    if (state.status === TenantProcessStatus.Completed || state.status === TenantProcessStatus.Failed) {
      return true;
    }

    if (state.conversation?.thread?.some((item) => (item.contentBlocks?.length ?? 0) > 0) === true) {
      return true;
    }

    const responseCount = state.conversation?.thread?.filter((item) => !!item.response).length ?? 0;

    return responseCount > 2;
  }

  private resolveAuditPeriod(appContext: IAppContext): { periodStart?: string; periodEnd?: string } {
    try {
      const raw = getLocalStorageData(appContext, LocalStorageKeys.TaskPeriodSelector);
      if (!raw) return {};

      const data = JSON.parse(raw) as IPeriodFilterData;
      if (data.auditYear === undefined) {
        const yearRaw = getLocalStorageData(appContext, LocalStorageKeys.AuditYear);
        if (yearRaw) data.auditYear = Number.parseInt(yearRaw);
      }

      const range = getPeriodFilter(data, appContext);

      return { periodStart: range.start?.toISOString(), periodEnd: range.end?.toISOString() };
    } catch {
      return {};
    }
  }

  private isControlAuditAction(action: CopilotAction): boolean {
    return this.isControlAuditProcess(action.processId);
  }

  private isControlAuditProcess(processId: TenantProcess | undefined): boolean {
    return processId === TenantProcess.ControlEvidenceAudit || processId === TenantProcess.ControlDocumentationAudit;
  }
}
